import React, {Component} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import moment from 'moment-timezone';
import IconExclamationCircle from '@spothero/icons/exclamation-circle';
import Button from '@spothero/ui/button';
import DateUtils from 'utils/date';
import CancellationInfo from 'common/cancellation-info/CancellationInfo';
import Card from 'common/card/Card';
import CardContentPanel from 'common/card/CardContentPanel';
import PaymentMethodsIcons from 'common/payment-methods-icons/PaymentMethodsIcons';
import Price from 'common/price/Price';
import ParkingDateTimePanel from './ParkingDateTimePanel';
import ReviewPurchase from 'checkout/summary/ReviewPurchase';
import AvailableInventory from './AvailableInventory';

export default class AirportPurchaseInformation extends Component {
    static propTypes = {
        facility: PropTypes.object,
        isFetchingRates: PropTypes.bool,
        starts: PropTypes.string,
        ends: PropTypes.string,
        availableTransientInventory: PropTypes.number,
        selectedRate: PropTypes.object,
        fetchRates: PropTypes.func.isRequired,
        isAirportRateUnavailable: PropTypes.bool,
        onCheckoutClick: PropTypes.func,
        pushTo: PropTypes.func,
        updateSelectedSpot: PropTypes.func,
        showDailyPrice: PropTypes.bool,
        displayPrice: PropTypes.number,
        currencyType: PropTypes.string,
        isShortenedDatepickerExperiment: PropTypes.bool,
        onShowConfirmDateTimeModal: PropTypes.func,
    };

    constructor(props) {
        super(props);

        const {isFetchingRates, starts, ends} = props;
        const startsMoment = starts ? moment(starts) : null;
        const endsMoment = ends ? moment(ends) : null;
        let isEditingDateTime = true;

        if (startsMoment && endsMoment && startsMoment.isBefore(endsMoment)) {
            isEditingDateTime = false;
        }

        this._DATE_TIME_FORMAT = 'MMM D [at] h:mma';

        this.state = {
            prevIsFetchingRates: isFetchingRates,
            isEditingDateTime,
        };
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        const {isFetchingRates} = nextProps;

        if (prevState.prevIsFetchingRates && !isFetchingRates) {
            return {
                prevIsFetchingRates: isFetchingRates,
                isEditingDateTime: false,
            };
        }

        return null;
    }

    _onDateTimeSearchClick = ({starts, ends}) => {
        const {facility, fetchRates} = this.props;
        const {id: spotId} = facility;

        fetchRates({
            spotId,
            starts,
            ends,
            airport: true,
            meta: {
                dateTimeChanged: true,
            },
        });
    };

    _onDateTimeEditClick = () => {
        const {
            isShortenedDatepickerExperiment,
            onShowConfirmDateTimeModal,
        } = this.props;

        if (isShortenedDatepickerExperiment) {
            onShowConfirmDateTimeModal(true);
        } else {
            this.setState({
                isEditingDateTime: true,
            });
        }
    };

    _onViewOtherOptionsClick = () => {
        const {
            facility: {
                airport: {
                    details: {url},
                },
            },
            starts,
            ends,
            pushTo,
            updateSelectedSpot,
        } = this.props;
        let searchUrl = `/${url}`;

        if (starts && ends) {
            searchUrl += `?airport=true&starts=${starts}&ends=${ends}`;
        }

        updateSelectedSpot();
        pushTo(searchUrl);
    };

    _renderDateTimePreview(section) {
        const {starts, ends} = this.props;
        const {isEditingDateTime} = this.state;
        const dateTimeFormatted = DateUtils.getDateTimeSummary({
            startDateTimeObj: moment(starts),
            endDateTimeObj: moment(ends),
            isMonthly: false,
        });

        if (!isEditingDateTime) {
            return (
                <span className="CardContentPanel-preview-datetime">
                    {dateTimeFormatted}
                </span>
            );
        } else {
            return null;
        }
    }

    _renderDateTimeEdit() {
        return (
            <Button
                className="CardContentPanel-title-actions"
                onClick={this._onDateTimeEditClick}
            >
                Edit
            </Button>
        );
    }

    render() {
        const {
            isFetchingRates,
            isAirportRateUnavailable,
            starts,
            ends,
            facility: {cancellation_allowed: cancellationAllowed},
            selectedRate,
            availableTransientInventory,
            onCheckoutClick,
            showDailyPrice,
            displayPrice,
            currencyType,
            isShortenedDatepickerExperiment,
        } = this.props;
        const {isEditingDateTime} = this.state;
        const datetimePreview = this._renderDateTimePreview();
        const dateTimePanelClasses = classNames('Panel-date-time', {
            'Panel-completed': !isEditingDateTime,
        });

        return (
            <Card className="AirportPurchaseInformation">
                {!isShortenedDatepickerExperiment && (
                    <CardContentPanel
                        className={dateTimePanelClasses}
                        title="Reservation Time"
                        additionalTitleMarkup={
                            !isEditingDateTime
                                ? this._renderDateTimeEdit()
                                : null
                        }
                        preview={datetimePreview}
                        isOpen={isEditingDateTime}
                        showContentOnPrint={false}
                    >
                        <ParkingDateTimePanel
                            isFetchingRates={isFetchingRates}
                            starts={starts}
                            ends={ends}
                            onSearchClick={this._onDateTimeSearchClick}
                        />
                    </CardContentPanel>
                )}
                {isShortenedDatepickerExperiment && (
                    <div className="CardContentPanel Panel-date-time Panel-completed">
                        <div className="CardContentPanel-title">
                            <h2 className="CardContentPanel-title-heading">
                                <span>Reservation Time</span>
                            </h2>
                            {this._renderDateTimeEdit()}
                        </div>
                        <div className="CardContentPanel-preview">
                            <span className="CardContentPanel-preview-datetime">
                                {datetimePreview}
                            </span>
                        </div>
                    </div>
                )}
                <AvailableInventory
                    availableTransientInventory={availableTransientInventory}
                />
                {selectedRate && !showDailyPrice && (
                    <ReviewPurchase showTaxesAndFeesMessage />
                )}
                {selectedRate && showDailyPrice && (
                    <div className="AirportPurchaseInformation-daily-price">
                        <span className="price-per-day">
                            <Price
                                amount={displayPrice}
                                currencyType={currencyType}
                            />{' '}
                            <span className="per-day">Per Day</span>
                        </span>
                        <small className="price-basis">
                            Price is based on a 24 hour period
                        </small>
                    </div>
                )}
                <Button
                    className="AirportPurchaseInformation-book-now"
                    color="primary"
                    disabled={!selectedRate}
                    onClick={onCheckoutClick}
                    block
                >
                    Book Now
                </Button>
                <PaymentMethodsIcons showAppleAndGooglePay />
                <CancellationInfo
                    className="AirportPurchaseInformation-no-cancellation"
                    cancellationAllowed={cancellationAllowed}
                    showFreeCancellation
                />
                {isAirportRateUnavailable && (
                    <div className="AirportPurchaseInformation-unavailable">
                        <IconExclamationCircle />
                        <p>
                            We&apos;re sorry, this spot isn&apos;t available at
                            the selected times.
                        </p>
                        <Button
                            color="primary"
                            block
                            onClick={this._onViewOtherOptionsClick}
                        >
                            View Other Options
                        </Button>
                    </div>
                )}
            </Card>
        );
    }
}
